import { useMemo, useState } from "react";
import { Search, Check, Lock, ChevronDown } from "lucide-react";
import styles from "./PermissionMatrix.module.css";
import {
  groupPermissionsByModule,
  getActionMeta,
  humanize,
} from "../../constants/permissionMeta";

/**
 * Reusable module → resource → action permission grid.
 *
 * Props:
 *  - permissions: flat list of all permission objects ({ id, module, resource, action, key, description })
 *  - value: array of selected permission ids (directly toggleable)
 *  - onChange: (ids:number[]) => void
 *  - inheritedIds: array of permission ids granted indirectly (e.g. via a role) — shown locked
 *  - readOnly: render as a non-editable summary
 */
const PermissionMatrix = ({
  permissions = [],
  value = [],
  onChange,
  inheritedIds = [],
  readOnly = false,
}) => {
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const selected = useMemo(() => new Set(value.map(Number)), [value]);
  const inherited = useMemo(() => new Set(inheritedIds.map(Number)), [inheritedIds]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? permissions.filter(
          (p) =>
            p.key?.toLowerCase().includes(q) ||
            p.description?.toLowerCase().includes(q) ||
            p.module?.toLowerCase().includes(q)
        )
      : permissions;
    return groupPermissionsByModule(filtered);
  }, [permissions, query]);

  const totalSelected = selected.size;

  const toggle = (id) => {
    if (readOnly || inherited.has(Number(id))) return;
    const next = new Set(selected);
    if (next.has(Number(id))) next.delete(Number(id));
    else next.add(Number(id));
    onChange?.([...next]);
  };

  const moduleIds = (group) =>
    group.permissions.map((p) => Number(p.id)).filter((id) => !inherited.has(id));

  const moduleState = (group) => {
    const ids = moduleIds(group);
    if (ids.length === 0) return "none";
    const on = ids.filter((id) => selected.has(id)).length;
    if (on === 0) return "none";
    if (on === ids.length) return "all";
    return "some";
  };

  const toggleModule = (group) => {
    if (readOnly) return;
    const ids = moduleIds(group);
    const next = new Set(selected);
    const state = moduleState(group);
    if (state === "all") ids.forEach((id) => next.delete(id));
    else ids.forEach((id) => next.add(id));
    onChange?.([...next]);
  };

  const selectAll = () => {
    if (readOnly) return;
    const next = new Set(selected);
    groups.forEach((g) => moduleIds(g).forEach((id) => next.add(id)));
    onChange?.([...next]);
  };

  const clearAll = () => {
    if (readOnly) return;
    const visible = new Set();
    groups.forEach((g) => moduleIds(g).forEach((id) => visible.add(id)));
    onChange?.([...selected].filter((id) => !visible.has(id)));
  };

  return (
    <div className={styles.matrix}>
      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <Search size={15} className={styles.searchIcon} />
          <input
            className={styles.search}
            placeholder="Search permissions…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className={styles.summary}>
          <span className={styles.countPill}>{totalSelected} selected</span>
          {!readOnly && (
            <>
              <button type="button" className={styles.linkBtn} onClick={selectAll}>
                Select all
              </button>
              <button type="button" className={styles.linkBtn} onClick={clearAll}>
                Clear
              </button>
            </>
          )}
        </div>
      </div>

      {groups.length === 0 ? (
        <div className={styles.empty}>No permissions match your search.</div>
      ) : (
        <div className={styles.groups}>
          {groups.map((group) => {
            const state = moduleState(group);
            const isCollapsed = collapsed[group.key];
            const onCount = group.permissions.filter(
              (p) => selected.has(Number(p.id)) || inherited.has(Number(p.id))
            ).length;

            // group permissions by resource for compact rows
            const byResource = {};
            group.permissions.forEach((p) => {
              if (!byResource[p.resource]) byResource[p.resource] = [];
              byResource[p.resource].push(p);
            });

            return (
              <div key={group.key} className={styles.group}>
                <div className={styles.groupHeader} style={{ borderColor: group.accent }}>
                  <button
                    type="button"
                    className={styles.groupTitle}
                    onClick={() =>
                      setCollapsed((c) => ({ ...c, [group.key]: !c[group.key] }))
                    }
                  >
                    <ChevronDown
                      size={16}
                      className={`${styles.chev} ${isCollapsed ? styles.chevClosed : ""}`}
                    />
                    <span className={styles.groupIcon}>{group.icon}</span>
                    <span className={styles.groupLabel}>{group.label}</span>
                    <span className={styles.groupCount}>
                      {onCount}/{group.permissions.length}
                    </span>
                  </button>
                  {!readOnly && (
                    <button
                      type="button"
                      className={`${styles.moduleToggle} ${
                        state === "all"
                          ? styles.moduleAll
                          : state === "some"
                          ? styles.moduleSome
                          : ""
                      }`}
                      onClick={() => toggleModule(group)}
                    >
                      {state === "all" ? "Deselect all" : "Select all"}
                    </button>
                  )}
                </div>

                {!isCollapsed && (
                  <div className={styles.resourceList}>
                    {Object.entries(byResource).map(([resource, perms]) => (
                      <div key={resource} className={styles.resourceRow}>
                        <div className={styles.resourceName}>{humanize(resource)}</div>
                        <div className={styles.pills}>
                          {perms
                            .slice()
                            .sort((a, b) => a.action.localeCompare(b.action))
                            .map((p) => {
                              const id = Number(p.id);
                              const isInherited = inherited.has(id);
                              const isOn = selected.has(id) || isInherited;
                              const am = getActionMeta(p.action);
                              return (
                                <button
                                  key={p.id}
                                  type="button"
                                  title={p.description || p.key}
                                  className={`${styles.pill} ${isOn ? styles.pillOn : ""} ${
                                    isInherited ? styles.pillInherited : ""
                                  }`}
                                  style={
                                    isOn && !isInherited
                                      ? { background: am.bg, color: am.color, borderColor: am.color }
                                      : undefined
                                  }
                                  onClick={() => toggle(p.id)}
                                  disabled={readOnly || isInherited}
                                >
                                  {isInherited ? (
                                    <Lock size={11} />
                                  ) : isOn ? (
                                    <Check size={12} />
                                  ) : null}
                                  {am.label}
                                </button>
                              );
                            })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PermissionMatrix;
